import { useAutocomplete } from "@mui/base/useAutocomplete";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import classNames from "classnames";
import { type FocusEvent, useLayoutEffect, useRef, useState } from "react";
import { isIOS } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useFirstMountState } from "react-use";
import { Popper } from "components/autocomplete/base/popper";
import { Input } from "components/input";
import { type Destination } from "utils/google-places/destination";
import { mixpanelTrack } from "utils/tracking/mixpanel";
import { type AutocompletePlace, useGooglePlacesAutocomplete } from "../utils";
import {
  AlertIcon,
  Component,
  Footer,
  Paper,
  ResultsFromGoogle,
} from "./styles";

export interface AutocompleteProps {
  autoFocus?: boolean;
  name: string;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange: (value: Destination) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  selectedPlaces: Destination[];
}

export function Autocomplete({
  autoFocus,
  name,
  onBlur,
  onChange,
  onFocus,
  selectedPlaces,
}: AutocompleteProps) {
  const { t } = useTranslation("screens", {
    keyPrefix: "destination.autocomplete",
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [value, setValue] = useState<AutocompletePlace | null>(null);

  // DEV-8916: This hack fixes an issue on iOS where the highlight background of
  // the keyboard suggestions is not removed after a destination is selected and
  // the input value is set to an empty string. iOS doesn't allow accessing
  // these suggestions programatically — only user interactions with the
  // keyboard can affect them. They can't be disabled too, but removing the
  // input element from the DOM, attaching a new one, and focusing it
  // immediately, will reset the suggestions without closing the keyboard. Since
  // React doesn't allow us to manipulate the DOM tree this way, this hack will
  // hide the Input component while rerenderHack is set to true. Immediately,
  // useLayoutEffect below will set it to false, which will render the component
  // again — as a new DOM element. Then, useLayoutEffect will run one more time
  // to keep the focus on the new element, keeping the keyboard opened.
  const [rerenderHack, setRerenderHack] = useState(false);

  const isFirstMount = useFirstMountState();

  const { options } = useGooglePlacesAutocomplete(query, selectedPlaces);

  const { getInputProps, getListboxProps, getOptionProps, getRootProps } =
    useAutocomplete({
      autoHighlight: true,
      clearOnBlur: true,
      filterOptions: (options) => options,
      freeSolo: true,
      getOptionDisabled: (option) => !!option.disabled,
      onChange: (_, value) => {
        if (typeof value === "string" || !value?.placeId) {
          return;
        }

        onChange(value);
        setValue(null);

        if (isIOS) {
          setRerenderHack(true);
        }

        const optionIndex = options?.findIndex(
          ({ placeId }) => placeId === value.placeId,
        );

        mixpanelTrack("autocomplete suggestion selected", {
          "google places api type":
            value.types
              ?.filter((type) => type !== "geocode" && type !== "political")
              ?.join(", ") || "n/a",
          "position in selected destinations": selectedPlaces.length + 1,
          "position in suggestions list":
            (optionIndex !== undefined &&
              optionIndex > -1 &&
              `${optionIndex + 1} of ${options?.length}`) ||
            "n/a",
          "query length": query.length,
          "search query text": query,
          "selected destination name": value.description,
          "selected destination place id": value.placeId,
        });
      },
      onInputChange: (_, value, reason) => {
        setQuery(reason === "input" ? value : "");
      },
      options: options ?? [],
      value,
    });

  const { ref: inputRef, ...inputProps } = getInputProps();

  useLayoutEffect(() => {
    if (isFirstMount) {
      // Do nothing...
    } else if (rerenderHack) {
      setRerenderHack(false);
    } else if (inputRef && "current" in inputRef) {
      inputRef.current?.focus();
    }
  }, [rerenderHack]); // eslint-disable-line react-hooks/exhaustive-deps

  const showResults = options !== null && options.length > 0;

  return (
    <Component ref={containerRef} {...getRootProps()}>
      {!rerenderHack && (
        <Input
          aria-label={t("placeholder")}
          autoCapitalize="none"
          autoCorrect="off"
          autoFocus={autoFocus}
          inputProps={{
            ...inputProps,
            enterKeyHint: "go",
            ref: inputRef,
            value: query,
          }}
          name={name}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={t("placeholder")}
          variant="simple"
        />
      )}

      <Popper
        anchorEl={containerRef.current}
        open={!!options?.length}
        style={{ width: containerRef.current?.offsetWidth }}
      >
        <Paper className={autocompleteClasses.paper}>
          {showResults && (
            <ul {...getListboxProps()} className={autocompleteClasses.listbox}>
              {options.map((option, index) => {
                const { className, ...optionProps } = getOptionProps({
                  option,
                  index,
                });

                return (
                  <li
                    {...optionProps}
                    className={classNames(
                      autocompleteClasses.option,
                      className,
                      { disabled: !!option.disabled },
                    )}
                    key={option.placeId}
                  >
                    {option.description}
                    {option.disabled && (
                      <>
                        {` | ${t("unavailableOptionSuffix")}`}
                        <AlertIcon />
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <Footer>{!!options?.length && <ResultsFromGoogle />}</Footer>
        </Paper>
      </Popper>
    </Component>
  );
}
